import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { planningApi } from '@/apis/planning.api'
import { scheduleApi } from '@/apis/schedule.api'
import { accountingApi } from '@/apis/accounting.api'
import { useAccountingStore } from '@/stores/accounting.store'
import { quarterStart, quarterEnd } from '@/composables/date-range-presets'
import { registerKpiCacheClearer } from '@/composables/kpi-refresh'
import { permissionFlagEnum } from '@/stores/portal.store'

dayjs.extend(isoWeek)

const fmt = (v) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(v)
const fmtPct = (v) => `${v}%`

// Converts tile filter config { warehouseIds, periodType, periodFrom, periodTo } → API query params
export const toKpiApiParams = (filters = {}) => {
  const { warehouseIds, periodFrom, periodTo } = filters
  const periodType = (!filters.periodType || filters.periodType === 'all') ? 'month' : filters.periodType
  const params = {}
  if (warehouseIds?.length) params.warehouse_ids = warehouseIds.join(',')
  if (periodType === 'custom') {
    if (periodFrom) params.date_from = periodFrom
    if (periodTo) params.date_to = periodTo
    return params
  }
  const today = dayjs()
  const ranges = {
    week: { from: today.startOf('isoWeek'), to: today.startOf('isoWeek').add(1, 'week') },
    month: { from: today.startOf('month'), to: today.add(1, 'month').startOf('month') },
    quarter: { from: quarterStart(today), to: quarterEnd(today).add(1, 'day').startOf('day') },
    year: { from: today.startOf('year'), to: today.add(1, 'year').startOf('year') },
  }
  const r = ranges[periodType]
  if (r) {
    params.date_from = r.from.format('YYYY-MM-DD')
    params.date_to = r.to.format('YYYY-MM-DD')
  }
  return params
}

// Several metrics read different fields of the same summary endpoint. Without sharing, a strip
// with 4 project metrics fires 4 identical requests per page load. This de-duplicates in-flight
// calls and keeps the response for a short window, so one endpoint is hit once per param set.
const SUMMARY_CACHE_TTL_MS = 15000
const summaryCache = new Map()

const cachedSummary = (source, apiCall, params) => {
  const key = `${source}:${JSON.stringify(params ?? {})}`
  const hit = summaryCache.get(key)
  if (hit && Date.now() - hit.ts < SUMMARY_CACHE_TTL_MS) return hit.promise
  const promise = apiCall(params).catch(err => { summaryCache.delete(key); throw err })
  summaryCache.set(key, { ts: Date.now(), promise })
  return promise
}

registerKpiCacheClearer(() => summaryCache.clear())

const projectSummary = (filters) => cachedSummary('project-active', planningApi.getProjectActiveSummaryAsync, toKpiApiParams(filters))
const equipmentSummary = (filters) => cachedSummary('equipment-attention', planningApi.getEquipmentAttentionCountAsync, toKpiApiParams(filters))
const budgetSummary = (filters) => cachedSummary('budget-kpi', accountingApi.getBudgetDocumentKpiSummaryAsync, toKpiApiParams(filters))
const staffSummary = (filters) => cachedSummary('staff-available', planningApi.getStaffAvailableTodayAsync, toKpiApiParams(filters))
const conflictsSummary = () => cachedSummary('conflicts', scheduleApi.getConflictsSummaryAsync, undefined)
const expenseClaimSummary = (filters) => cachedSummary('expense-claim-status', accountingApi.getExpenseClaimStatusCountsAsync, toKpiApiParams(filters))
const reportSummary = () => cachedSummary('report-kpi', () => accountingApi.getAsync(`${accountingApi.reportsUrl}kpi-summary/`), undefined)
const taskCounts = () => cachedSummary('task-counts', () => scheduleApi.getAsync(`${scheduleApi.tasksUrl}counts/`), undefined)

// Frontend-only executable half of each KPI metric: how to fetch its value and how to
// format it. The display metadata (titleKey/labelKey/link/supports*) lives in the backend
// catalog (planning.DashboardKpiMetric) and is merged in by the dashboard store, keyed by
// this same metric_key.
export const KPI_METRIC_REGISTRY = Object.freeze({
  active_projects: {
    fetch: async(filters) => (await projectSummary(filters)).data.active,
    format: v => String(v),
  },
  avg_duration: {
    fetch: async(filters) => (await projectSummary(filters)).data.avg_duration_days,
    format: v => String(v),
  },
  equipment_attention: {
    fetch: async(filters) => (await equipmentSummary(filters)).data.count,
    format: v => String(v),
  },
  scheduling_conflicts: {
    fetch: async() => (await conflictsSummary()).data.total,
    format: v => String(v),
  },
  revenue_this_month: {
    fetch: async(filters) => (await budgetSummary(filters)).data.revenue_this_month,
    format: v => fmt(v),
  },
  completed_this_month: {
    fetch: async(filters) => (await projectSummary(filters)).data.completed_this_month,
    format: v => String(v),
  },
  completed_projects: {
    fetch: async(filters) => (await projectSummary(filters)).data.completed,
    format: v => String(v),
  },
  quotes_this_month: {
    fetch: async(filters) => (await budgetSummary(filters)).data.quotes_this_month,
    format: v => String(v),
  },
  open_quotes: {
    fetch: async(filters) => (await budgetSummary(filters)).data.open_quotes,
    format: v => String(v),
  },
  projects_ending: {
    fetch: async(filters) => (await projectSummary(filters)).data.projects_ending,
    format: v => String(v),
  },
  utilisation_rate: {
    fetch: async(filters) => (await equipmentSummary(filters)).data.utilisation_rate,
    format: v => fmtPct(v),
  },
  staff_available_today: {
    fetch: async(filters) => (await staffSummary(filters)).data.available,
    format: v => String(v),
  },
  overdue_projects: {
    fetch: async(filters) => (await projectSummary(filters)).data.overdue,
    format: v => String(v),
  },
  overdue_tasks: {
    fetch: async() => (await taskCounts()).data.overdue,
    format: v => String(v),
  },
  gross_margin_this_month: {
    fetch: async(filters) => (await budgetSummary(filters)).data.gross_margin_pct,
    format: v => (v !== null ? fmtPct(v) : '—'),
  },
  equipment_in_repair: {
    fetch: async(filters) => (await equipmentSummary(filters)).data.in_repair,
    format: v => String(v),
  },
  equipment_needs_cleaning: {
    fetch: async(filters) => (await equipmentSummary(filters)).data.needs_cleaning,
    format: v => String(v),
  },
  new_projects_this_month: {
    fetch: async(filters) => (await projectSummary(filters)).data.new_this_month,
    format: v => String(v),
  },
  expense_claims_pending: {
    fetch: async(filters) => (await expenseClaimSummary(filters)).data[useAccountingStore().expenseClaimStatusEnum.SUBMITTED],
    format: v => String(v),
  },
  reports_pending_recompute: {
    fetch: async() => (await reportSummary()).data.pending_recompute,
    format: v => String(v),
  },
})

const PERIOD_LABEL_KEYS = {
  week: 'dashboard.filterPeriodWeek',
  month: 'dashboard.filterPeriodMonth',
  quarter: 'dashboard.filterPeriodQuarter',
  year: 'dashboard.filterPeriodYear',
}

const fmtDate = (iso) => {
  if (!iso) return '?'
  const [y, m, d] = iso.split('-')
  return `${parseInt(d)}/${parseInt(m)}/${String(y).slice(-2)}`
}

export const getPeriodLabel = (filters, t) => {
  const { periodType, periodFrom, periodTo } = filters ?? {}
  const type = (!periodType || periodType === 'all') ? 'month' : periodType
  if (PERIOD_LABEL_KEYS[type]) return t(PERIOD_LABEL_KEYS[type])
  if (type === 'custom' && (periodFrom || periodTo)) {
    return `${fmtDate(periodFrom)} - ${fmtDate(periodTo)}`
  }
  return t(PERIOD_LABEL_KEYS.month)
}

export const KPI_CHART_TYPE_ENUM = Object.freeze({ LINE: 0, SPLINE: 1, BAR: 2 })
export const KPI_CHART_PERIOD_ENUM = Object.freeze({ THIRTY_DAYS: 0, QUARTERLY: 1, YEARLY: 2 })

// Tone of a signal-bar value → Quasar semantic color. Attention uses a single warning color,
// never a warning/critical gradient (see frontend.md "Status signals: one warning color").
export const signalToneEnum = Object.freeze({ NEUTRAL: 0, ATTENTION: 1 })

const SIGNAL_TONE_COLOR = Object.freeze({
  [signalToneEnum.NEUTRAL]: 'primary',
  [signalToneEnum.ATTENTION]: 'warning',
})

export const signalToneColor = (tone) => SIGNAL_TONE_COLOR[tone] ?? SIGNAL_TONE_COLOR[signalToneEnum.NEUTRAL]

const attentionWhenSet = (v) => (v > 0 ? signalToneEnum.ATTENTION : signalToneEnum.NEUTRAL)

// Signal-bar display extras, merged into the metric metadata by the dashboard store. Keyed by the
// same metric_key as KPI_METRIC_REGISTRY and the backend catalog. `tone` colors a value that needs
// attention (and surfaces the warning icon); `unit: 'currency'` renders the value with the tenant's
// localized currency symbol; `permission` hides the metric from users without it. Only metrics that need one appear here.
export const SIGNAL_METRIC_META = Object.freeze({
  projects_ending: { tone: attentionWhenSet },
  scheduling_conflicts: { tone: attentionWhenSet },
  overdue_projects: { tone: attentionWhenSet },
  overdue_tasks: { tone: attentionWhenSet },
  equipment_attention: { tone: attentionWhenSet },
  equipment_in_repair: { tone: attentionWhenSet },
  equipment_needs_cleaning: { tone: attentionWhenSet },
  expense_claims_pending: { tone: attentionWhenSet },
  reports_pending_recompute: { tone: attentionWhenSet },
  revenue_this_month: { unit: 'currency', permission: permissionFlagEnum.FINANCIALS },
  gross_margin_this_month: { permission: permissionFlagEnum.FINANCIALS },
})

// Which existing dashboard KPI metrics each page's signal bar surfaces.
export const SIGNAL_BAR_PAGE_METRICS = Object.freeze({
  projects: ['active_projects', 'avg_duration', 'projects_ending', 'scheduling_conflicts', 'overdue_projects', 'overdue_tasks'],
  equipments: ['equipment_needs_cleaning', 'utilisation_rate', 'equipment_in_repair'],
  staffs: ['staff_available_today', 'scheduling_conflicts', 'active_projects'],
  quotes: ['open_quotes', 'quotes_this_month', 'revenue_this_month', 'gross_margin_this_month'],
  companies: ['active_projects', 'revenue_this_month', 'gross_margin_this_month', 'completed_projects', 'new_projects_this_month'],
  reports: ['reports_pending_recompute', 'expense_claims_pending'],
})
